/**
 * “TA 惦记着”的读模型（合同 TRV-00 §38.3，用户 2026-09-27「需要惦记」）：GET /travel/longing 的那一条惦记 ＋ GET /travel/wish 的当前心愿，
 * 换成页面要的样子。纯函数：不读服务、不碰页面（页面这一版先不显示惦记，视觉等 r7k 的参考稿）。
 * 口径（I、A、B 定）：
 * - 惦记另占一格：不占当前心愿那一格，可以和一个能去的心愿同时在；每只宠物最多惦记一处，新的替换旧的（后端换，这里只读当前那一条）。
 * - 惦记期间不查资料、不出计划、不出门——所以这里只有地名、城市、地区、理由、从哪天开始惦记，没有“还差什么”“攒钱”“出发”。
 * - 这里写出来的字不许承诺（“很快就能去”“以后带 TA 去”这类），用例里有文案守卫（claude-6c2b-longing.test.tsx）。
 * - “当前心愿”按后端“进行中”的口径（A 的 OPEN_STATES = active、ready；攻略架“想去 / 准备中”同一口径）：GET /travel/wish 没有进行中的心愿时
 *   给的是最近一条（出发了、回来了、放下了），那一条不算当前心愿。
 */
import type { TravelLonging, TravelWish } from "@/shared/contracts";
import { formatDay, PROVISIONAL_ZONE, type ViewOptions } from "./model";

/** 没有惦记：明确的一种状态（GET /travel/longing 答 null），不是“还没读到”。 */
export interface NoLonging {
  kind: "none";
}

/** TA 惦记着的那一处。 */
export interface LongingView {
  kind: "longing";
  longingId: string;
  /** 惦记的地方（place_name 去掉首尾空白），如“东京”“浅水湾”“台场的海边”；空的（后端不会给）退回城市。 */
  place: string;
  /** 城市（去掉首尾空白）；空的就是 null。 */
  city: string | null;
  /** 地区（国家或地区，可空）；空的就是 null。 */
  region: string | null;
  /** 显示用的地名：地名和城市互相包含（“东京”与“东京”、“东京塔”与“东京”）就只写地名，否则“城市·地名”（东京·台场的海边）。 */
  name: string;
  /** TA 给主人的一句理由（owner_reason 去掉首尾空白）；空的就是 null，不编。 */
  reason: string | null;
  /** 从哪天开始惦记（created_at）：“2026年9月26日”，按 TA 所在城市的时区取日期；认不出来就是 null，不伪造。 */
  since: string | null;
  /** 最朴素的一行：“{名字} 惦记着 {地名}”（名字拿不到写“TA”）。 */
  line: string;
}

export type LongingRead = NoLonging | LongingView;

/** 当前心愿与惦记并存的四种组合。 */
export type WishLongingState = "both" | "longing_only" | "wish_only" | "neither";

export interface WishLongingView {
  state: WishLongingState;
  /** 进行中的当前心愿（active / ready）；没有就是 null（GET /travel/wish 给的最近一条不算，见文件开头）。 */
  wish: TravelWish | null;
  longing: LongingRead;
}

export type LongingOptions = Pick<ViewOptions, "petName" | "timezone">;

const NONE: NoLonging = { kind: "none" };

/** 进行中的心愿才算“当前心愿”（后端 OPEN_STATES）。 */
export function isCurrentWish(wish: TravelWish | null | undefined): wish is TravelWish {
  return wish != null && (wish.status === "active" || wish.status === "ready");
}

function textOf(value: string | null | undefined): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed ? trimmed : null;
}

function nameOf(place: string | null, city: string | null): string | null {
  if (!place) return city;
  if (!city || place.includes(city) || city.includes(place)) return place;
  return `${city}·${place}`;
}

/** 时区认不出来（Intl 报错）就用暂定的 PROVISIONAL_ZONE，与计划页同一口径。 */
function sinceOf(value: string | null | undefined, timezone: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  try {
    return formatDay(value, timezone || PROVISIONAL_ZONE);
  } catch {
    return formatDay(value, PROVISIONAL_ZONE);
  }
}

/**
 * 一条惦记 → 页面的样子。null / undefined 是“没有惦记”。
 * 地名与城市都空的（后端保证不会，A 的 _clean 不许空）也当没有惦记：没有地方可说，就不写半句话。
 */
export function viewOfLonging(longing: TravelLonging | null | undefined, options: LongingOptions = {}): LongingRead {
  if (!longing) return NONE;
  const place = textOf(longing.place_name);
  const city = textOf(longing.city);
  const name = nameOf(place, city);
  if (!name) return NONE;
  const who = options.petName?.trim() || "TA";
  return {
    kind: "longing",
    longingId: longing.longing_id,
    place: place ?? name,
    city,
    region: textOf(longing.region),
    name,
    reason: textOf(longing.owner_reason),
    since: sinceOf(longing.created_at, options.timezone),
    line: `${who} 惦记着 ${name}`,
  };
}

/** 当前心愿（GET /travel/wish）与惦记（GET /travel/longing）放在一起：四种组合各有自己的 state。 */
export function wishAndLonging(wish: TravelWish | null | undefined, longing: TravelLonging | null | undefined, options: LongingOptions = {}): WishLongingView {
  const current = isCurrentWish(wish) ? wish : null;
  const view = viewOfLonging(longing, options);
  const longs = view.kind === "longing";
  const state: WishLongingState = current ? (longs ? "both" : "wish_only") : longs ? "longing_only" : "neither";
  return { state, wish: current, longing: view };
}
